import { useEffect, useState } from "react";
import {
  Filter,
  RefreshCw,
  Eye,
  RotateCcw,
  MoreVertical,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";
import {
  fetchOutboxMessages,
  retryMessage,
  setAutoRefresh,
  setOutboxFilters,
} from "../../store/features/outbox/outboxActions";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../store/features/ui/uiActions";
import MessagePreviewModal from "../components/outbox/MessagePreviewModal";
import { useTranslation } from "react-i18next";

export function OutboxPage() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const {
    filteredMessages,
    filters,
    autoRefresh,
    isLoading,
    isRetrying,
    error,
  } = useAppSelector((state) => state.outbox);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Preview modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);

  // Calculate pagination
  const totalItems = filteredMessages.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentMessages = filteredMessages.slice(startIndex, endIndex);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    dispatch(fetchOutboxMessages());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(showErrorNotification(error.message));
    }
  }, [error, dispatch]);

  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        dispatch(fetchOutboxMessages());
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh, dispatch]);

  const handleFilterChange = (filterName, value) => {
    dispatch(setOutboxFilters({ [filterName]: value }));
  };

  const handleAutoRefreshToggle = (enabled) => {
    dispatch(setAutoRefresh(enabled));
  };

  const handleRetry = async (messageId) => {
    try {
      await dispatch(retryMessage(messageId));
      dispatch(showSuccessNotification(t("message_queued_for_retry")));
    } catch (error) {
      dispatch(showErrorNotification(t("failed_to_retry_message")));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchOutboxMessages());
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handlePreview = (messageId) => {
    setSelectedMessageId(messageId);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setSelectedMessageId(null);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      queued: "status-badge status-queued",
      processing: "status-badge status-processing",
      sent: "status-badge status-sent",
      failed: "status-badge status-failed",
    };

    const icons = {
      queued: "🟠",
      processing: "🔵",
      sent: "🟢",
      failed: "🔴",
    };

    return (
      <span className={statusClasses[status]}>
        {/* <span className="me-1">{icons[status]}</span> */}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getMediaIcon = (media) => {
    const icons = {
      sms: "📱",
      email: "📧",
      whatsapp: "💬",
      telegram: "✈️",
    };
    return icons[media];
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="container-fluid">
      {/* Filters and Controls */}
      <div className=" mb-4">
        <div
          className="card-body"
          style={{ backgroundColor: "white", borderRadius: "5px" }}
        >
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="row g-3 align-items-center">
                <div className="col-auto">
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                    className="form-select form-select-sm"
                  >
                    <option value="all">{t("all_status")}</option>
                    <option value="queued">{t("queued")}</option>
                    <option value="processing">{t("processing")}</option>
                    <option value="sent">{t("sent")}</option>
                    <option value="failed">{t("failed")}</option>
                  </select>
                </div>

                <div className="col-auto">
                  <select
                    value={filters.media}
                    onChange={(e) =>
                      handleFilterChange("media", e.target.value)
                    }
                    className="form-select form-select-sm"
                  >
                    <option value="all">{t("all_media")}</option>
                    <option value="sms">{t("sms")}</option>
                    <option value="email">{t("email")}</option>
                    <option value="whatsapp">{t("whatsapp")}</option>
                    <option value="telegram">{t("telegram")}</option>
                  </select>
                </div>

                <div className="col-auto">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="autoRefresh"
                      checked={autoRefresh}
                      onChange={(e) =>
                        handleAutoRefreshToggle(e.target.checked)
                      }
                      className="form-check-input"
                    />
                    <label
                      htmlFor="autoRefresh"
                      className="form-check-label small"
                    >
                      {t("auto_refresh")}
                    </label>
                  </div>
                </div>

                <div className="col-auto">
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(parseInt(e.target.value))
                    }
                    className="form-select form-select-sm"
                  >
                    <option value={10}>{t("10_per_page")}</option>
                    <option value={25}>{t("25_per_page")}</option>
                    <option value={50}>{t("50_per_page")}</option>
                    <option value={100}>{t("100_per_page")}</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="col-md-4 text-end">
              <div className="d-flex gap-2 justify-content-end">
                {/* <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2">
                  <Download size={16} />
                  Export
                </button> */}
                <button
                  onClick={handleRefresh}
                  className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <RefreshCw size={16} />
                  {t("refresh")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Table */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th className="fw-semibold">{t("recipient")}</th>
                <th className="fw-semibold">{t("media")}</th>
                <th className="fw-semibold">{t("status")}</th>
                <th className="fw-semibold">{t("preview")}</th>
                <th className="fw-semibold">{t("time")}</th>
                <th className="text-center fw-semibold">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {currentMessages.map((message) => (
                <tr key={message.id}>
                  <td>
                    <div className="fw-medium text-dark">
                      {message.recipient}
                    </div>
                    {message.error && (
                      <small className="text-danger">{message.error}</small>
                    )}
                  </td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {/* <span style={{ fontSize: "1.2em" }}>
                        {getMediaIcon(message.media)}
                      </span> */}
                      <span className="text-capitalize small">
                        {message.media}
                      </span>
                    </div>
                  </td>
                  <td>{getStatusBadge(message.status)}</td>

                  <td>
                    <div
                      className="small text-muted line-clamp-2"
                      style={{ maxWidth: "300px" }}
                    >
                      {message.message}
                    </div>
                  </td>
                  <td>
                    <small className="text-muted">
                      {formatTimestamp(message.created_at)}
                    </small>
                  </td>
                  <td>
                    <div className="d-flex justify-content-center gap-1">
                      <button className="btn btn-sm btn-outline-secondary">
                        <Eye
                          size={14}
                          onClick={() => handlePreview(message.id)}
                        />
                      </button>
                      {message.status === "failed" && (
                        <button
                          onClick={() => handleRetry(message.id)}
                          className="btn btn-sm btn-outline-success"
                          disabled={isRetrying}
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                      {/* <div className="dropdown">
                        <button
                          className="btn btn-sm btn-outline-secondary dropdown-toggle"
                          type="button"
                          data-bs-toggle="dropdown"
                        >
                          <MoreVertical size={14} />
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <a className="dropdown-item" href="#">
                              View Details
                            </a>
                          </li>
                          <li>
                            <a className="dropdown-item" href="#">
                              Download
                            </a>
                          </li>
                          <li>
                            <hr className="dropdown-divider" />
                          </li>
                          <li>
                            <a className="dropdown-item text-danger" href="#">
                              Delete
                            </a>
                          </li>
                        </ul>
                      </div> */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="card-footer">
            <div className="row align-items-center">
              <div className="col-md-6">
                <div className="text-muted small">
                  {t("showing")} {startIndex + 1} {t("to")}{" "}
                  {Math.min(endIndex, totalItems)} {t("of")} {totalItems}{" "}
                  {t("entries")}
                </div>
              </div>
              <div className="col-md-6">
                <nav aria-label="Table pagination">
                  <ul className="pagination pagination-sm justify-content-end mb-0">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft size={16} />
                      </button>
                    </li>

                    {getPageNumbers().map((page, index) => (
                      <li
                        key={index}
                        className={`page-item ${
                          page === currentPage ? "active" : ""
                        } ${page === "..." ? "disabled" : ""}`}
                      >
                        {page === "..." ? (
                          <span className="page-link">...</span>
                        ) : (
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        )}
                      </li>
                    ))}

                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      <MessagePreviewModal
        messageId={selectedMessageId}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
      {/* Summary Stats */}
      <div className="row g-3 mt-4">
        <div className="col-md-3">
          <div className="card bg-success bg-opacity-10 border-success border-opacity-25">
            <div className="card-body text-center">
              <div className="display-6 fw-bold text-success">
                {filteredMessages.filter((m) => m.status === "sent").length}
              </div>
              <div className="small text-success">{t("messages_sent")}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-warning bg-opacity-10 border-warning border-opacity-25">
            <div className="card-body text-center">
              <div className="display-6 fw-bold text-warning">
                {filteredMessages.filter((m) => m.status === "queued").length}
              </div>
              <div className="small text-warning">{t("in_queue")}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div
            className="card  bg-opacity-10 border-primary border-opacity-25"
            style={{ backgroundColor: "#fcedcf" }}
          >
            <div className="card-body text-center">
              <div className="display-6 fw-bold text-primary">
                {
                  filteredMessages.filter((m) => m.status === "processing")
                    .length
                }
              </div>
              <div className="small text-primary">{t("processing")}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-danger bg-opacity-10 border-danger border-opacity-25">
            <div className="card-body text-center">
              <div className="display-6 fw-bold text-danger">
                {filteredMessages.filter((m) => m.status === "failed").length}
              </div>
              <div className="small text-danger">{t("failed")}</div>
            </div>
          </div>
        </div>
      </div>

      {filteredMessages.length === 0 && (
        <div className="text-center py-5">
          <div
            className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
            style={{ width: "64px", height: "64px" }}
          >
            <Filter className="text-muted" size={32} />
          </div>
          <h5 className="fw-medium text-dark mb-2">{t("no_messages_found")}</h5>
          <p className="text-muted">{t("try_adjusting_filters")}</p>
        </div>
      )}
    </div>
  );
}
